import React, { useRef, useEffect } from 'react';
import { VariableSizeList as List } from 'react-window';
import { classNames } from 'primereact/utils';
import { debounce } from 'lodash';

import { useUserDetailsContext } from 'context/userDetailsContext';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import constants from 'constants/index';
import Message from './Message';
import useDivProperties from 'hooks/useDivProperties';

const InfiniteScrollList = ({
  currentOffset,
  hasMoreMessages,
  listRef,
  messages,
  loading,
  selectedThread,
  fetchMessageHistory,
  scrollContainerRef,
}) => {
  const userContext = useUserDetailsContext();
  const rowHeights = useRef({});
  const [divRef, divProperties] = useDivProperties();
  const previousMessagesLength = useRef(0);
  const isInitialLoad = useRef(true);

  const isMessageLoaded = (index) => index < messages.length;

  // Scroll to bottom on initial load or when new messages are added (not pagination)
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || messages.length === 0) return;

    // Check if this is initial load (messages went from 0 to some number)
    // or if new messages were appended at the end (message count increased and offset is still 0)
    const isNewMessageAdded = messages.length > previousMessagesLength.current && currentOffset === 0;

    if (isInitialLoad.current || isNewMessageAdded) {
      // Scroll to bottom with a slight delay to ensure DOM is rendered
      // Use requestAnimationFrame for better timing with DOM updates
      const scrollToBottom = () => {
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
      };

      // Use multiple attempts to ensure scroll happens after render
      requestAnimationFrame(() => {
        scrollToBottom();
        // Additional timeout as fallback for slower renders
        setTimeout(scrollToBottom, 150);
      });

      isInitialLoad.current = false;
    }

    previousMessagesLength.current = messages.length;
  }, [messages.length, currentOffset]);

  // Reset initial load flag when thread changes
  useEffect(() => {
    isInitialLoad.current = true;
    previousMessagesLength.current = 0;

    // Also scroll to bottom immediately when thread changes (for when messages are already loaded)
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      requestAnimationFrame(() => {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      });
    }
  }, [selectedThread?.contact_id]);

  const loadMoreMessages = () => {
    if (!loading && hasMoreMessages && selectedThread?.contact_id) {
      fetchMessageHistory(selectedThread?.contact_id, false, selectedThread?.to?.replace('+1', '')?.replace('+91', ''), currentOffset);
    }
  };

  // Scroll listener for manual scroll detection
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = debounce(() => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

      // Trigger load when scrolled near the top (10% from top)
      // Since messages are newest at bottom, scrolling up loads older messages
      if (scrollTop < 100 && !loading && hasMoreMessages) {
        loadMoreMessages();
      }
    }, 300);

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [loading, hasMoreMessages, currentOffset, selectedThread]);

  function setRowHeight(index, size) {
    listRef.current.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [index]: size };
  }

  function getRowHeight(index) {
    const message = messages[index];
    const t1 = convertDateFromApiToUserTimezone(message?.created_at, userContext?.userDetails?.timezone, constants.month_date_year_format);

    const t2 = convertDateFromApiToUserTimezone(
      messages[index - 1]?.created_at,
      userContext?.userDetails?.timezone,
      constants.month_date_year_format
    );
    const isGroupHeader = t1 !== t2;
    return rowHeights.current[index] + (isGroupHeader ? 35 : 1) || 150;
  }

  const Row = ({ index, style }) => {
    if (!isMessageLoaded(index)) {
      return null;
    }
    const message = messages[index];

    const t1 = convertDateFromApiToUserTimezone(message?.created_at, userContext?.userDetails?.timezone, constants.month_date_year_format);

    const t2 = convertDateFromApiToUserTimezone(
      messages[index - 1]?.created_at,
      userContext?.userDetails?.timezone,
      constants.month_date_year_format
    );

    const isGroupHeader = t1 !== t2;

    return (
      <div
        style={{ ...style, borderBottom: 'none' }}
        className={classNames({ 'msg-container': true, 'mb-0 pt-0 pb-0': true })}
        id="msgContainer"
      >
        {isGroupHeader && (
          <div className="text-center msg-filter">
            <span>
              {convertDateFromApiToUserTimezone(message?.created_at, userContext.userDetails.timezone, constants.month_date_year_format)}
            </span>
          </div>
        )}
        <Message {...{ selectedThread, message, index, setRowHeight }} />
      </div>
    );
  };

  const renderLoader = () => {
    return (
      <div className="chat-loader-container">
        <div className="overlay">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2em' }}></i>
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'relative' }}>
      {loading && renderLoader()}

      {messages.map((message, index) => {
        const t1 = new Date(message.created_at).toDateString();
        const t2 = index > 0 ? new Date(messages[index - 1].created_at).toDateString() : null;
        const isGroupHeader = t1 !== t2;

        return (
          <div key={message.id || index} className={classNames('msg-container mb-0 pt-0 pb-0')} id="msgContainer">
            {isGroupHeader && (
              <div className="text-center msg-filter">
                <span>{t1}</span>
              </div>
            )}
            <Message {...{ selectedThread, message, index, setRowHeight: () => {} }} />
          </div>
        );
      })}

      {/* Show "Load More" indicator at top when there are more messages */}
      {hasMoreMessages && !loading && messages.length > 0 && (
        <div className="text-center py-2 text-muted" style={{ fontSize: '12px' }}>
          Scroll up to load older messages
        </div>
      )}
    </div>
  );
};

export default InfiniteScrollList;
